import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react"
import { User } from "../models/user"
import UserService from "../services/user"
import useNotyf from "./useNotify"

interface UserContextType {
  user: User | null
  logout: () => void
  setUser: (user: User | null) => void
  tokenLogin: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: UserProviderProps) {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)

  const notyf = useNotyf()

  useEffect(() => {

    const delayInMilliseconds = 2 // Fix: Timing error

    const timeoutId = setTimeout(() => {
      tokenLogin()
    }, delayInMilliseconds)

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeoutId)
  }, [])

  const logout = useCallback(() => {
    UserService.logout()
    setUser(null)
  }, [])

  const tokenLogin = async () => {
    try {
      const { user } = await UserService.checkToken()
      setUser(user)
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <UserContext.Provider
      value={{
        user,
        logout,
        setUser,
        tokenLogin
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider")
  }
  return context
}

interface UserProviderProps {
  children: ReactNode
}
